import Pagination from '../../components/Common/Pagination';
import { TableLoader, Spinner } from '../../components/Common/Loader';
import React, { useState, useMemo, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { getPackagings, createPackaging, updatePackaging } from '../../api/productMasters';
import CSVButton from '../../components/Common/CSVButton';
import { exportToCSV } from '../../utils/csvExport';

interface Packaging {
  id: number;
  name: string;
  erpId?: string;
  packagingType?: string;
  crateSize?: number | null;
  unitsPerCrate?: number | null;
  status: 'Active' | 'Inactive';
  createdAt?: string | null;
}

export const PackagingList: React.FC = () => {
  const [packagings, setPackagings] = useState<Packaging[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [view, setView] = useState<'list' | 'add' | 'edit' | 'view'>('list');
  const [editingItem, setEditingItem] = useState<Packaging | null>(null);
  const [viewingItem, setViewingItem] = useState<Packaging | null>(null);

  const formatDate = (dateStr?: string | null): string => {
    if (!dateStr) return '-';
    if (dateStr.includes('T')) {
      const parts = dateStr.split('T');
      const time = parts[1].split('.')[0];
      return `${parts[0]} ${time}`;
    }
    return dateStr;
  };

  // Fetch Packagings from API
  const fetchPackagingsData = async () => {
    try {
      setLoading(true);
      const res = await getPackagings();
      if (res && (res.status === 'success' || res.success) && res.data) {
        const rawList = Array.isArray(res.data)
          ? res.data
          : ((res.data as any)?.data || (res.data as any)?.packagings || []);

        if (Array.isArray(rawList)) {
          const mapped: Packaging[] = rawList.map((item: any) => ({
            id: item.id,
            name: item.name || item.packagingName || '',
            erpId: item.sapCode || item.product?.materialCode || item.erpId || '-',
            packagingType: item.packagingType || '',
            crateSize: item.crateSize ?? null,
            unitsPerCrate: item.unitsPerCrate ?? null,
            status: (item.status === 1 || item.status === '1' || item.status === 'Active') ? 'Active' : 'Inactive',
            createdAt: item.createdAt || item.created_at || item.created || null
          }));
          setPackagings(mapped);
        }
      }
    } catch (err) {
      console.error('Error fetching packagings list:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackagingsData();
  }, []);

  const [activeTab, setActiveTab] = useState<'Active' | 'Inactive' | 'All'>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [entriesLimit, setEntriesLimit] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    erpId: '',
    packagingType: '',
    crateSize: '',
    unitsPerCrate: '',
    status: 'Active' as 'Active' | 'Inactive',
  });

  // Dynamic active/inactive counts
  const activeCounts = useMemo(() => packagings.filter(p => p.status === 'Active').length, [packagings]);
  const inactiveCounts = useMemo(() => packagings.filter(p => p.status === 'Inactive').length, [packagings]);

  // Filtering Logic
  const filteredPackagings = useMemo(() => {
    return packagings.filter((item) => {
      if (activeTab === 'Active' && item.status !== 'Active') return false;
      if (activeTab === 'Inactive' && item.status !== 'Inactive') return false;

      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          item.name.toLowerCase().includes(query) ||
          (item.createdAt && item.createdAt.toLowerCase().includes(query))
        );
      }
      return true;
    });
  }, [packagings, activeTab, searchQuery]);

  // Paginated Items
  const paginatedPackagings = useMemo(() => {
    const startIndex = (currentPage - 1) * entriesLimit;
    return filteredPackagings.slice(startIndex, startIndex + entriesLimit);
  }, [filteredPackagings, currentPage, entriesLimit]);

  const handleOpenAdd = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      erpId: '',
      packagingType: '',
      crateSize: '',
      unitsPerCrate: '',
      status: 'Active',
    });
    setView('add');
  };

  const handleOpenEdit = (item: Packaging) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      erpId: item.erpId === '-' ? '' : (item.erpId || ''),
      packagingType: item.packagingType || '',
      crateSize: item.crateSize != null ? String(item.crateSize) : '',
      unitsPerCrate: item.unitsPerCrate != null ? String(item.unitsPerCrate) : '',
      status: item.status || 'Active',
    });
    setView('edit');
  };

  const handleOpenView = (item: Packaging) => {
    setViewingItem(item);
    setView('view');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) {
      alert('Name is required.');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        name: formData.name,
        packagingName: formData.name,
        sapCode: formData.erpId || undefined,
        packagingType: formData.packagingType || undefined,
        crateSize: formData.crateSize ? Number(formData.crateSize) : undefined,
        unitsPerCrate: formData.unitsPerCrate ? Number(formData.unitsPerCrate) : undefined,
        status: formData.status === 'Active' ? 1 : 0,
      };

      if (editingItem) {
        // Update Packaging: PUT /api/v1/erp/products/packagings/:id
        await updatePackaging(editingItem.id, payload);
      } else {
        // Create Packaging: POST /api/v1/erp/products/packagings
        await createPackaging(payload);
      }

      await fetchPackagingsData();
      setActiveTab('All');
      setView('list');
    } catch (err: any) {
      console.error('Error saving packaging:', err);
      alert(err.message || 'Failed to save packaging. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (view === 'view' && viewingItem) {
    return (
      <div className="space-y-6 animate-fadeIn">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <button 
              type="button"
              onClick={() => setView('list')}
              className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm" 
              title="Go Back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight">
              View Packagings
            </h1>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-2.5">
            <button 
              onClick={handleOpenAdd}
              className="go_erp-bg text-white text-xs font-bold py-2 px-4 rounded-xl flex items-center gap-2 cursor-pointer hover:opacity-90 transition-opacity"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Add Packaging
            </button>
            <button 
              onClick={() => handleOpenEdit(viewingItem)}
              className="bg-[#F9C327] hover:bg-[#E5B21E] text-white text-xs font-bold py-2 px-4 rounded-xl flex items-center gap-2 cursor-pointer transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit Packaging
            </button>
            <button 
              onClick={() => {
                if (window.confirm('Are you sure you want to delete this packaging?')) {
                  setPackagings(packagings.filter(p => p.id !== viewingItem.id));
                  setView('list');
                }
              }}
              className="bg-[#4E342E] hover:bg-[#3E2723] text-white text-xs font-bold py-2 px-4 rounded-xl flex items-center gap-2 cursor-pointer transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete Packaging
            </button>
          </div>
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-2xl p-6">
          <div className="text-sm font-bold text-slate-800 mb-2">
            Packaging Details
          </div>
          <div className="border-b border-[#ECECF2] mb-6"></div>

          {/* Details Table/Grid */}
          <div className="overflow-x-auto select-none">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr>
                  <th className="pb-3 text-xs font-semibold text-slate-400 w-1/4">ID</th>
                  <th className="pb-3 text-xs font-semibold text-slate-400 w-1/4">Name</th>
                  <th className="pb-3 text-xs font-semibold text-slate-400 w-1/4">Created At</th>
                  <th className="pb-3 text-xs font-semibold text-slate-400 w-1/4">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="pt-2 text-xs font-bold text-slate-700">{viewingItem.id}</td>
                  <td className="pt-2 text-xs font-bold text-slate-800">{viewingItem.name}</td>
                  <td className="pt-2 text-xs font-bold text-slate-800">{formatDate(viewingItem.createdAt)}</td>
                  <td className="pt-2 text-xs font-bold text-slate-800">{viewingItem.status}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'add' || view === 'edit') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <button 
              type="button"
              onClick={() => setView('list')}
              className="w-7 h-7 rounded-full go_erp-bg transition-all flex items-center justify-center text-white cursor-pointer" title="Go Back"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <h3 className="text-xl font-bold go_erp-text tracking-wide">
              {view === 'edit' ? 'Edit Packaging' : 'Add Packagings'}
            </h3>
          </div>
        </div>

        {/* Form Container */}
        <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-2xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            {/* Name */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Name*</label>
              <input 
                type="text" 
                required
                placeholder="e.g. Carton, Pouch"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800 bg-white" 
              />
            </div>

            {/* Status Dropdown */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Status</label>
              <div className="relative">
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as 'Active' | 'Inactive' })}
                  className="w-full bg-[#F5F5F5] border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800 appearance-none cursor-pointer"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
                <span className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-center pt-4">
            <button 
              type="submit" 
              disabled={submitting}
              className="bg-[#DCA10D] hover:bg-[#c28c00] text-white font-bold py-3 px-16 rounded-xl text-sm transition-all cursor-pointer shadow-md hover:shadow-[#DCA10D]/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting && <Spinner size="sm" color="text-white" />}
              {submitting ? 'Saving...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  const handleExportCSV = () => {
    const headers = ['ID', 'Name', 'ERP ID', 'Packaging Type', 'Crate Size', 'Units/Crate', 'Status'];
    const rows = filteredPackagings.map((p) => [
      p.id,
      p.name,
      p.erpId || '-',
      p.packagingType || '-',
      p.crateSize ?? '-',
      p.unitsPerCrate ?? '-',
      p.status,
    ]);
    exportToCSV(`packagings_${activeTab.toLowerCase()}`, headers, rows);
  };

  return (
    <div className="space-y-6">
      {/* Control Row */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        {/* Title and Back */}
        <div className="flex items-center gap-3">
          <button 
            onClick={() => window.history.back()}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm" title="Go Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight">
            Packagings
          </h1>
        </div>

        {/* Status Tabs */}
        <div className="flex bg-white p-1 rounded-full border border-slate-200 w-fit">
          {(['All', 'Active', 'Inactive'] as const).map((tab) => {
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-1.5 rounded-full text-xs font-bold transition-all duration-150 cursor-pointer ${
                  isActive
                    ? 'go_erp-bg text-white shadow-sm'
                    : 'text-slate-655 hover:text-slate-900'
                }`}
              >
                {tab}
              </button>
            );
          })}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2.5">
          <button
            onClick={handleOpenAdd}
            className="go_erp-bg text-white text-sm px-4 py-1.5 rounded-xl flex items-center gap-2 cursor-pointer transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Add Packagings
          </button>
          <CSVButton onClick={handleExportCSV} />
        </div>
      </div>

      {/* Counts */}
      <div className="flex items-center justify-end gap-4 text-xs font-bold text-slate-800">
        <span>Active Counts: <span className="font-extrabold">{activeCounts}</span></span>
        <span>Inactive Counts: <span className="font-extrabold">{inactiveCounts}</span></span>
      </div>

      {/* Table Main Card */}
      <div className="bg-white rounded-2xl overflow-hidden">
        
        {/* Table Toolbar */}
        <div className="px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-slate-50/50">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-500">Show</span>
            <select
              value={entriesLimit}
              onChange={(e) => setEntriesLimit(Number(e.target.value))}
              className="bg-white border border-slate-200 text-xs font-bold text-slate-700 rounded px-2 py-1 outline-none focus:border-slate-400"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span className="text-xs font-semibold text-slate-500">entries</span>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-white border border-slate-200 text-xs rounded-full py-1.5 pl-8 pr-3 outline-none focus:border-slate-400 w-44 transition-all"
              />
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </span>
            </div>
          </div>
        </div>

        {/* Table Wrapper */}
        <div className="overflow-x-auto select-none">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#FFF8E1]">
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">ID</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">Name</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">Created At</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">Status</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f] text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#ECECF2]">
              {loading ? (
                <TableLoader colSpan={5} text="Loading..." />
              ) : paginatedPackagings.length > 0 ? (
                paginatedPackagings.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-4 text-xs text-[#828282]">{item.id}</td>
                    <td className="px-4 py-4 text-xs text-[#828282] font-semibold">{item.name}</td>
                    <td className="px-4 py-4 text-xs text-[#828282]">{formatDate(item.createdAt)}</td>
                    <td className="px-4 py-4 text-xs text-[#828282]">{item.status}</td>
                    <td className="px-4 py-4 text-xs text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => handleOpenView(item)} className="p-1.5 text-slate-500 hover:text-[#B71C1C] transition-colors cursor-pointer rounded-lg" title="View">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                          </button>
                          <button onClick={() => handleOpenEdit(item)} className="p-1.5 text-slate-500 hover:text-blue-600 transition-colors cursor-pointer rounded-lg" title="Edit">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                          </button>
                        </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-xs font-semibold text-slate-400">
                    No data available in table
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <Pagination
          currentPage={currentPage}
          totalEntries={filteredPackagings.length}
          entriesLimit={entriesLimit}
          onPageChange={setCurrentPage}
        />

      </div>
    </div>
  );
};
export default PackagingList;
